import { Box, CircularProgress, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, {
    FC, createContext, ForwardedRef, forwardRef, Suspense,
    useContext, useEffect,
    useMemo,
    useRef, useState
} from "react";
import { useNavigate } from "react-router";
import { ConfirmacaoProvider } from "../confirmacao/confirmacao";
import "./browser.css";
import { BackHandlerProvider, useBackHandler } from "./backhandler";
import PageRender from "./pagerender/pagerender";
import PageBody from "./taskbar/PageBody/pagebody";
import TaskBar from "./taskbar/taskbar";
import { BrowserContextType, MenuItemType } from "./taskbar/taskbartypes.d";
import { criarTemaCustomizado, NomeTema } from "./taskbar/tema";
import { ZIndexProvider } from "./zindexcontext";
import useAuthGuard from "./hooks/UseAuthGuard";
import { useTokenRefresh } from "../../hooks/useTokenRefresh";

dayjs.locale('pt-br');
dayjs.extend(customParseFormat);

const EasyBrowserContext = createContext<BrowserContextType | undefined>(undefined);
export const useEasyBrowserContext = () => useContext(EasyBrowserContext);

// Registra o handler de fechar aba via botão back. Vive dentro do BackHandlerProvider.
const BrowserBackHandlerEffect: FC<{
  taskSelecionada: MenuItemType | null;
  fecharPagina: (item: MenuItemType) => void;
  dashboardId: string;
}> = ({ taskSelecionada, fecharPagina, dashboardId }) => {
  const backHandler = useBackHandler();
  const refSelecionada = useRef(taskSelecionada);
  refSelecionada.current = taskSelecionada;
  const refFechar = useRef(fecharPagina);
  refFechar.current = fecharPagina;

  useEffect(() => {
    if (!backHandler) return;
    return backHandler.pushHandler(() => {
      const current = refSelecionada.current;
      if (!current || current.id === dashboardId) return false;
      refFechar.current(current);
      return true;
    });
  // pushHandler é estável (useCallback sem deps)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backHandler, dashboardId]);

  return null;
};

const Browser = forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
    const pagainaDashboard: MenuItemType = {
        id: 'easy.schedule.menu.principal',
        descricao: 'Menu Principal',
        url: '/menu/menu',
        icone: "MenuIcon",
        grupo: ['principal'],
        licenca: ['GERAL']
    };

    const { carregando: verificandoSessao, usuario } = useAuthGuard();
    useTokenRefresh();

    const [listaTasks, setListaTasks] = useState<MenuItemType[]>([]);
    const [taskSelecionada, setTaskSelecionada] = useState<MenuItemType | null>(null);
    const [paginasRenderizadas, setPaginasRenderizadas] = useState<any[]>([]);
    const pagesRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const idsAbertos = useRef<Set<string>>(new Set());

    const [notaEntrada, setNotaEntrada] = useState<any>(null);
    const [notaSaida, setNotaSaida] = useState<any>(null);
    const [cte, setCte] = useState<any>(null);

    const [configTema, setConfigTema] = useState<NomeTema>('slate');
    const temaAtualizado = useMemo(() => criarTemaCustomizado(configTema), [configTema]);
    const navigate = useNavigate();

    // ── Título da aba do navegador ────────────────────────────────────────────
    useEffect(() => {
        if (!verificandoSessao && usuario?.empresaID) {
            const nome = usuario.empresaID.charAt(0).toUpperCase() + usuario.empresaID.slice(1);
            document.title = `Gestão App Codesh (${nome})`;
        }
    }, [verificandoSessao, usuario]);

    // ── Abre dashboard inicial após autenticação ───────────────────────────────
    useEffect(() => {
        if (!verificandoSessao && listaTasks.length === 0) {
            abrirPagina(pagainaDashboard);
        }
    }, [verificandoSessao]);

    // ── Visibilidade das abas (translateX para evitar reset de z-index) ────────
    useEffect(() => {
        Object.keys(pagesRef.current).forEach((key) => {
            const el = pagesRef.current[key];
            if (!el) return;
            const ativa = key === taskSelecionada?.id;
            el.style.transform = ativa ? 'translateX(0)' : 'translateX(-200%)';
            el.style.position = 'absolute';
            el.style.inset = '0';
            el.style.visibility = ativa ? 'visible' : 'hidden';
            el.style.pointerEvents = ativa ? 'auto' : 'none';
        });
    }, [taskSelecionada, paginasRenderizadas]);

    // ── Gerenciamento de abas ─────────────────────────────────────────────────

    const fecharPagina = (item: MenuItemType) => {
        idsAbertos.current.delete(item.id);
        const novaLista = listaTasks.filter(t => t.id !== item.id);
        setPaginasRenderizadas(prev => prev.filter(p => p.props.paginaID !== item.id));
        setListaTasks(novaLista);
        setTaskSelecionada(novaLista.at(-1) || null);
    };

    const abrirPagina = async (item: MenuItemType) => {
        const jaAberta = listaTasks.find(t => t.id === item.id);
        if (!jaAberta && !idsAbertos.current.has(item.id)) {
            idsAbertos.current.add(item.id);
            setListaTasks(prev => [...prev, item]);
            setPaginasRenderizadas(prev => [...prev, buildPagina(item)]);
        }
        setTaskSelecionada(item);
    };

    const getTask = (id: string) => listaTasks.filter(t => t.id === id);

    const fecharTodasPaginas = () => {
        const idsParaManter = new Set([pagainaDashboard.id]);
        listaTasks.forEach(t => { if (!idsParaManter.has(t.id)) idsAbertos.current.delete(t.id); });
        setPaginasRenderizadas(prev => prev.filter(p => idsParaManter.has(p.props.paginaID)));
        setListaTasks(prev => prev.filter(t => idsParaManter.has(t.id)));
        setTaskSelecionada(pagainaDashboard);
    };

    const buildPagina = (item: MenuItemType) => {
        const PageComponent = React.lazy(() => import("../../paginas/gestao" + item.url));
        return (
            <PageRender
                key={item.id}
                ref={(el: any) => { if (el) pagesRef.current[item.id] = el; }}
                paginaID={item.id}
                registro={item.registro}
            >
                <Suspense fallback={<Box sx={{ p: 3 }}>Carregando tela...</Box>}>
                    <ZIndexProvider>
                        <ConfirmacaoProvider>
                            <PageComponent />
                        </ConfirmacaoProvider>
                    </ZIndexProvider>
                </Suspense>
            </PageRender>
        );
    };

    // ── Splash screen de verificação de sessão ────────────────────────────────
    if (verificandoSessao) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
                <CircularProgress size={32} sx={{ color: '#82b07d' }} />
            </Box>
        );
    }

    // ── Render principal ──────────────────────────────────────────────────────
    return (
        <EasyBrowserContext.Provider value={{
            listaTasks, abrirPagina, getTask, fecharPagina, fecharTodasPaginas, taskSelecionada,
            paginasRenderizadas, usuario,
            notaEntrada, setNotaEntrada,
            notaSaida, setNotaSaida,
            cte, setCte,
            configTema, setConfigTema,
        }}>
            <BackHandlerProvider>
                <BrowserBackHandlerEffect
                    taskSelecionada={taskSelecionada}
                    fecharPagina={fecharPagina}
                    dashboardId={pagainaDashboard.id}
                />
                <ThemeProvider theme={temaAtualizado}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100vh',
                            width: '100vw',
                            overflow: 'hidden',
                            bgcolor: 'background.default',
                        }}>
                            <TaskBar />
                            <PageBody />
                        </Box>
                    </LocalizationProvider>
                </ThemeProvider>
            </BackHandlerProvider>
        </EasyBrowserContext.Provider>
    );
});

export default Browser;